import React, { createContext, useContext, useState, useCallback } from 'react';
import identityService from '../services/identityService';
import { DEFAULT_PAGE_SIZE } from '../utils/constants';

// Create context
const IdentityContext = createContext(null);

/**
 * Custom hook to use identity context
 * @returns {Object} Identity context value
 */
export const useIdentityContext = () => {
  const context = useContext(IdentityContext);
  if (!context) {
    throw new Error('useIdentityContext must be used within IdentityProvider');
  }
  return context;
};

/**
 * Identity Provider Component
 * Provides global state for identity management
 */
export const IdentityProvider = ({ children }) => {
  // Global state
  const [stats, setStats] = useState(null);
  const [selectedIdentity, setSelectedIdentity] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [dataSource, setDataSource] = useState('unknown');
  
  // Global filters (for maintaining state across navigation)
  const [globalFilters, setGlobalFilters] = useState({
    page: 1,
    limit: DEFAULT_PAGE_SIZE,
    search: '',
    type: null,
    status: null,
    riskLevel: null,
    sortBy: 'displayName',
    sortOrder: 'asc'
  });

  // Fetch statistics
  const fetchStats = useCallback(async () => {
    try {
      const response = await identityService.getStats();
      if (response.success) {
        setStats(response.data);
        setDataSource(response.data.dataSource || 'unknown');
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, []);

  // Sync identities
  const syncIdentities = useCallback(async () => {
    setSyncing(true);
    try {
      const response = await identityService.syncIdentities();
      if (response.success) {
        await fetchStats();
        return { success: true, message: response.message };
      }
      return { success: false, message: response.error?.message || 'Sync failed' };
    } catch (error) {
      return { success: false, message: error.message || 'Sync failed' };
    } finally {
      setSyncing(false);
    }
  }, [fetchStats]);

  // Open identity detail modal
  const openIdentityDetail = useCallback((identity) => {
    setSelectedIdentity(identity);
    setIsDetailModalOpen(true);
  }, []);

  // Close identity detail modal
  const closeIdentityDetail = useCallback(() => {
    setSelectedIdentity(null);
    setIsDetailModalOpen(false);
  }, []);

  // Update global filters
  const updateGlobalFilters = useCallback((newFilters) => {
    setGlobalFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  // Reset global filters
  const resetGlobalFilters = useCallback(() => {
    setGlobalFilters({
      page: 1,
      limit: DEFAULT_PAGE_SIZE,
      search: '',
      type: null,
      status: null,
      riskLevel: null,
      sortBy: 'displayName',
      sortOrder: 'asc'
    });
  }, []);

  const value = {
    // State
    stats,
    selectedIdentity,
    isDetailModalOpen,
    syncing,
    dataSource,
    globalFilters,
    
    // Actions
    fetchStats,
    syncIdentities,
    openIdentityDetail,
    closeIdentityDetail,
    updateGlobalFilters,
    resetGlobalFilters
  };

  return (
    <IdentityContext.Provider value={value}>
      {children}
    </IdentityContext.Provider>
  );
};

export default IdentityContext;